import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  assign,
  each,
  find,
  isArray,
  isNil,
  isUndefined,
  reduce,
  remove
} from 'lodash';
import { CardColumnAccordionDisplayBase } from 'src/app/classes/base-classes/card-column/card-column-accordion-display/card-column-accordion-display-base';
import { AccordionCardCollection } from 'src/app/interfaces/accordion-card-collection';
import { Card } from 'src/app/interfaces/card';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-card-column-accordion-display-modal',
  templateUrl: './card-column-accordion-display-modal.component.html',
  styleUrls: ['./card-column-accordion-display-modal.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CardColumnAccordionDisplayModalComponent
  extends CardColumnAccordionDisplayBase
  implements OnInit
{
  constructor(
    protected activeModal: NgbActiveModal,
    protected ToastService: ToastService,
    protected ChangeDetectorRef: ChangeDetectorRef,
    protected SharedDataService: SharedDataService
  ) {
    super();
  }

  // Determines if accordion items start collapsed or open.
  @Input() collapsed: boolean = false;
  // If this is true, any selections made will be displayed as badges near the top of the modal.
  // Selecting the 'X' or close button on a badge will de-select the applicable selection.
  @Input() displaySelectionsAsTags: boolean = false;
  @Input() cancelButtonText = 'Cancel';
  @Input() continueButtonText = 'Continue';

  SelectionTags = new Set<Card>([]);
  limitReached: boolean = false;

  removeSelectionTag(SelectionTag: Card) {
    SelectionTag.isClicked = true;
    this.SelectionTags.delete(SelectionTag);
    this.checkLimit();
  }

  // See if this can be merged with card-column-display-modal
  ngOnInit(): void {
    each(
      this.accordionCardCollectionArray,
      (accordionCardCollection: AccordionCardCollection) => {
        const { cardCollection } = accordionCardCollection;
        each(cardCollection, (Card: Card) => {
          if (
            this.selectionModel &&
            Card.record &&
            isUndefined(Card.isSelected)
          ) {
            if (isArray(this.selectionModel.data)) {
              Card.isSelected = !isNil(
                find(this.selectionModel.data, (arrayValue) =>
                  Card.record.comparator(arrayValue, Card.record.value)
                )
              );
            } else {
              Card.isSelected =
                this.selectionModel.data[Card.record.recordField] ===
                Card.record.selectValue;
            }

            if (Card.isSelected) {
              this.SelectionTags.add(Card);
            }
          }
        });
      }
    );
  }

  cancel() {
    if (this.selectionModel) {
      return this.activeModal.close(null);
    }
    return this.activeModal.close();
  }

  continue() {
    if (this.selectionModel) {
      // If the selection model has a selectionLimit applied, then we only allow an amount of selections
      // less than or equal to the limit.
      if (this.selectionModel.selectionLimit) {
        const uniqueSelections: Set<Card> = new Set<Card>();
        const selectedCount = reduce(
          this.accordionCardCollectionArray,
          (accum, accordionCardCollection: AccordionCardCollection) => {
            const { cardCollection } = accordionCardCollection;
            return (
              accum +
              reduce(
                cardCollection,
                (accum, Card: Card) => {
                  if (!uniqueSelections.has(Card)) {
                    uniqueSelections.add(Card);
                    return Card.isSelected ? accum + 1 : accum;
                  } else {
                    return this.selectionModel
                      ?.ignoreDuplicatesForSelectionLimit
                      ? accum
                      : Card.isSelected
                        ? accum + 1
                        : accum;
                  }
                },
                0
              )
            );
          },
          0
        );

        if (selectedCount > this.selectionModel.selectionLimit) {
          throw this.ToastService.addErrorToast(
            'Error',
            `You may only make ${this.selectionModel.selectionLimit} selections.`,
            { returnError: true }
          );
        }
      }
      return this.activeModal.close(this.selectionModel.data);
    }
    return this.activeModal.close();
  }

  checkLimit() {
    const selectionLimit = this.selectionModel?.selectionLimit;
    const isAtLimit = this.SelectionTags.size === selectionLimit;

    if (isAtLimit) {
      if (!this.limitReached) {
        this.limitReached = true;
        return;
      }

      throw this.ToastService.addErrorToast(
        'Error',
        `You may only make ${selectionLimit} selections.`,
        { returnError: true }
      );
    }

    if (this.SelectionTags.size < selectionLimit!) {
      this.limitReached = false;
    }
  }

  // See if this can be merged with card-column-display-modal
  override cardClicked(Card: Card) {
    // If a selection model was passed to this component, we can populate
    // data within the selection model.
    if (this.selectionModel) {
      // If the card contains a record (which in this case is: ArraySelectOption)
      if (Card.record) {
        if (isArray(this.selectionModel.data)) {
          // If the card is selected, push the value (of ArraySelectOption) of the record onto the data array.
          if (Card.isSelected) {
            // If the selection type is set to 'single', remove all elements in the underlying data array.
            if (this.selectionModel.selectionType === 'single') {
              this.selectionModel.data.splice(
                0,
                this.selectionModel.data.length
              );
            }

            // Push the value of the ArraySelectOption onto the underlying data array.
            this.selectionModel.data.push(Card.record.value);
          } else {
            remove(this.selectionModel.data, (arrayValue) =>
              Card.record.comparator(arrayValue, Card.record.value)
            );
          }
        } else {
          if (Card.isSelected) {
            // If the selection type is set to 'single', go through all selectable options
            // and update the underlying dataObj to the 'deselectValue' that is supplied
            // to each ObjectFieldSelectOption record.
            if (this.selectionModel.selectionType === 'single') {
              each(
                this.accordionCardCollectionArray,
                (accordionCardCollection: AccordionCardCollection) => {
                  const { cardCollection } = accordionCardCollection;
                  each(cardCollection, (Card: Card) => {
                    assign(this.selectionModel!.data, {
                      [Card.record.recordField]: Card.record.deselectValue
                    });
                  });
                }
              );
            }

            // Apply the 'selectValue' that is supplied to the ObjectFieldSelectOption record
            // to the underlying dataObj.
            assign(this.selectionModel.data, {
              [Card.record.recordField]: Card.record.selectValue
            });
          } else {
            // If the card is de-selected, apply the deselect value (from ObjectFieldSelectOption)
            this.selectionModel.data[Card.record.recordField] =
              Card.record.deselectValue;
          }
        }
      }

      if (Card.isSelected) {
        this.SelectionTags.add(Card);
      } else {
        this.SelectionTags.delete(Card);
      }

      delete Card.isClicked;
      this.ChangeDetectorRef.detectChanges();
    }
  }
}
